import { useCallback, useEffect, useRef, useState } from "react";
import {
  buildDeepgramWsProtocols,
  createPcmCaptureWorkletUrl,
  STT_SAMPLE_RATE,
} from "./pcmAudio";

const FLUX_WS_URL = `wss://api.deepgram.com/v2/listen?model=flux-general-en&encoding=linear16&sample_rate=${STT_SAMPLE_RATE}`;
const RECONNECT_DELAYS_MS = [500, 1000, 2000];

// All Flux server-message knowledge lives here so a schema change is a
// one-function fix. Flux sends {type: "TurnInfo", event, transcript, ...}
// where event is Update | StartOfTurn | EagerEndOfTurn | TurnResumed | EndOfTurn.
const parseFluxMessage = (rawData) => {
  let payload;
  try {
    payload = JSON.parse(rawData);
  } catch (error) {
    return null;
  }
  if (payload?.type !== "TurnInfo") return null;

  return {
    event: payload.event || "",
    transcript: typeof payload.transcript === "string" ? payload.transcript : "",
  };
};

// Streams mic audio to Deepgram Flux and surfaces turn-taking events.
// The mic stays open for the whole session (thinking/speaking included) so
// StartOfTurn can drive barge-in; echoCancellation keeps the assistant's own
// voice out of the transcript.
const useDeepgramFluxStt = ({
  getAccessToken,
  onInterimTranscript,
  onSpeechStarted,
  onEndOfTurn,
  onError,
}) => {
  const [sttState, setSttState] = useState("idle");

  const wsRef = useRef(null);
  const streamRef = useRef(null);
  const audioContextRef = useRef(null);
  const workletNodeRef = useRef(null);
  const sessionActiveRef = useRef(false);
  const sessionGenerationRef = useRef(0);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const mutedRef = useRef(false);

  const callbacksRef = useRef({});
  callbacksRef.current = {
    getAccessToken,
    onInterimTranscript,
    onSpeechStarted,
    onEndOfTurn,
    onError,
  };

  const teardownAudio = useCallback(() => {
    workletNodeRef.current?.disconnect();
    workletNodeRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    audioContextRef.current?.close().catch(() => {});
    audioContextRef.current = null;
  }, []);

  const closeSocket = useCallback(() => {
    const ws = wsRef.current;
    wsRef.current = null;
    if (!ws) return;
    ws.onerror = null;
    ws.onmessage = null;
    if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
      ws.close();
    }
  }, []);

  const isCurrentSession = useCallback(
    (generation) =>
      sessionActiveRef.current &&
      sessionGenerationRef.current === generation,
    [],
  );

  const openSocket = useCallback(async (generation) => {
    const accessToken = await callbacksRef.current.getAccessToken();
    if (!isCurrentSession(generation)) return null;

    const ws = new WebSocket(FLUX_WS_URL, buildDeepgramWsProtocols(accessToken));
    ws.binaryType = "arraybuffer";
    wsRef.current = ws;

    try {
      await new Promise((resolve, reject) => {
        ws.onopen = resolve;
        ws.onerror = () =>
          reject(new Error("Voice recognition connection failed"));
        ws.onclose = () =>
          reject(new Error("Voice recognition connection closed"));
      });
    } catch (error) {
      if (wsRef.current === ws) wsRef.current = null;
      ws.onopen = null;
      ws.onerror = null;
      ws.onmessage = null;
      ws.onclose = null;
      if (
        ws.readyState === WebSocket.OPEN ||
        ws.readyState === WebSocket.CONNECTING
      ) {
        ws.close();
      }
      throw error;
    }

    if (!isCurrentSession(generation) || wsRef.current !== ws) {
      ws.onopen = null;
      ws.onerror = null;
      ws.onmessage = null;
      ws.onclose = null;
      if (
        ws.readyState === WebSocket.OPEN ||
        ws.readyState === WebSocket.CONNECTING
      ) {
        ws.close();
      }
      return null;
    }

    reconnectAttemptRef.current = 0;
    ws.onopen = null;
    ws.onerror = null;
    ws.onmessage = (event) => {
      const message = parseFluxMessage(event.data);
      if (!message) return;

      if (message.event === "Update" && message.transcript) {
        callbacksRef.current.onInterimTranscript?.(message.transcript);
      } else if (message.event === "StartOfTurn") {
        callbacksRef.current.onSpeechStarted?.(message.transcript);
      } else if (message.event === "EndOfTurn") {
        callbacksRef.current.onEndOfTurn?.(message.transcript.trim());
      }
      // EagerEndOfTurn / TurnResumed are a v2 latency optimization.
    };
    ws.onclose = () => {
      if (wsRef.current === ws) wsRef.current = null;
      if (!isCurrentSession(generation)) return;

      const attempt = reconnectAttemptRef.current;
      if (attempt >= RECONNECT_DELAYS_MS.length) {
        setSttState("error");
        callbacksRef.current.onError?.(
          new Error("Voice recognition disconnected"),
        );
        return;
      }
      reconnectAttemptRef.current = attempt + 1;
      reconnectTimerRef.current = setTimeout(() => {
        reconnectTimerRef.current = null;
        if (!isCurrentSession(generation)) return;
        openSocket(generation).catch((error) => {
          if (!isCurrentSession(generation)) return;
          setSttState("error");
          callbacksRef.current.onError?.(error);
        });
      }, RECONNECT_DELAYS_MS[attempt]);
    };
    return ws;
  }, [isCurrentSession]);

  const connect = useCallback(async () => {
    if (sessionActiveRef.current) return;
    sessionActiveRef.current = true;
    const generation = sessionGenerationRef.current + 1;
    sessionGenerationRef.current = generation;
    setSttState("connecting");

    let stream = null;
    let audioContext = null;
    let workletNode = null;

    const cleanUpLocalAudio = () => {
      workletNode?.disconnect();
      if (workletNodeRef.current === workletNode) {
        workletNodeRef.current = null;
      }
      stream?.getTracks().forEach((track) => track.stop());
      if (streamRef.current === stream) streamRef.current = null;
      audioContext?.close().catch(() => {});
      if (audioContextRef.current === audioContext) {
        audioContextRef.current = null;
      }
    };

    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      if (!isCurrentSession(generation)) {
        cleanUpLocalAudio();
        return;
      }
      streamRef.current = stream;

      audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;
      const workletUrl = createPcmCaptureWorkletUrl();
      try {
        await audioContext.audioWorklet.addModule(workletUrl);
      } finally {
        URL.revokeObjectURL(workletUrl);
      }
      if (!isCurrentSession(generation)) {
        cleanUpLocalAudio();
        return;
      }

      const ws = await openSocket(generation);
      if (!ws || !isCurrentSession(generation)) {
        cleanUpLocalAudio();
        return;
      }

      const source = audioContext.createMediaStreamSource(stream);
      workletNode = new AudioWorkletNode(audioContext, "pcm-capture-processor");
      if (!isCurrentSession(generation)) {
        cleanUpLocalAudio();
        return;
      }
      workletNodeRef.current = workletNode;
      workletNode.port.onmessage = (event) => {
        const ws = wsRef.current;
        if (!ws || ws.readyState !== WebSocket.OPEN) return;
        if (mutedRef.current) return;
        ws.send(event.data);
      };
      // The worklet only observes; no audible output path is connected.
      source.connect(workletNode);

      setSttState("listening");
    } catch (error) {
      cleanUpLocalAudio();
      if (!isCurrentSession(generation)) return;
      sessionActiveRef.current = false;
      sessionGenerationRef.current += 1;
      closeSocket();
      setSttState("error");
      throw error;
    }
  }, [openSocket, isCurrentSession, closeSocket]);

  const disconnect = useCallback(() => {
    sessionActiveRef.current = false;
    sessionGenerationRef.current += 1;
    clearTimeout(reconnectTimerRef.current);
    reconnectTimerRef.current = null;
    reconnectAttemptRef.current = 0;
    closeSocket();
    teardownAudio();
    setSttState("idle");
  }, [closeSocket, teardownAudio]);

  const setMuted = useCallback((muted) => {
    mutedRef.current = Boolean(muted);
  }, []);

  const getMicStream = useCallback(() => streamRef.current, []);

  useEffect(() => () => disconnect(), [disconnect]);

  return {
    connect,
    disconnect,
    setMuted,
    sttState,
    getMicStream,
  };
};

export default useDeepgramFluxStt;
